import { DEFAULT_RELAY_URL, normalizeRelayUrl } from "@yep-anywhere/shared";
import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { FileViewer, type FileViewerSource } from "../components/FileViewer";
import { createPublicShareFileViewerSource } from "../components/publicShareFileViewerSource";
import {
  type PublicShareContextValue,
  PublicShareProvider,
} from "../contexts/PublicShareContext";
import { useI18n } from "../i18n";

function parsePositiveInteger(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export function PublicShareFilePage() {
  const { t } = useI18n();
  const { secret } = useParams<{ secret: string }>();
  const [searchParams] = useSearchParams();
  const filePath = searchParams.get("path") ?? "";
  const projectId = searchParams.get("projectId");
  const relayUsername = searchParams.get("h") ?? "";
  const lineNumber = parsePositiveInteger(searchParams.get("line"));
  const lineEnd = parsePositiveInteger(searchParams.get("lineEnd"));
  const viewMode =
    searchParams.get("view") === "range" ? ("range" as const) : "full";

  const relayConfig = useMemo((): { error: string | null; url: string } => {
    try {
      return {
        error: null,
        url: normalizeRelayUrl(searchParams.get("r") ?? DEFAULT_RELAY_URL),
      };
    } catch (err) {
      return {
        error: err instanceof Error ? err.message : String(err),
        url: DEFAULT_RELAY_URL,
      };
    }
  }, [searchParams]);

  const publicShareContext = useMemo<PublicShareContextValue | null>(() => {
    if (!secret || !relayUsername) {
      return null;
    }
    return {
      projectId,
      relayUrl: relayConfig.url,
      relayUsername,
      secret,
    };
  }, [projectId, relayConfig.url, relayUsername, secret]);

  const shareHref = useMemo(() => {
    if (!secret) {
      return "/share";
    }
    const params = new URLSearchParams();
    if (relayUsername) params.set("h", relayUsername);
    if (relayConfig.url) params.set("r", relayConfig.url);
    if (projectId) params.set("projectId", projectId);
    const query = params.toString();
    return `/share/${encodeURIComponent(secret)}${query ? `?${query}` : ""}`;
  }, [projectId, relayConfig.url, relayUsername, secret]);

  const source = useMemo<FileViewerSource | null>(() => {
    if (!publicShareContext || !filePath || relayConfig.error) {
      return null;
    }
    return createPublicShareFileViewerSource(publicShareContext);
  }, [filePath, publicShareContext, relayConfig.error]);

  const content = (
    <div className="file-page">
      <div className="file-page-nav">
        <Link to={shareHref} className="file-page-back-link">
          <span>{t("publicShareBackToShare" as never)}</span>
        </Link>
      </div>
      <div className="file-page-content">
        {source ? (
          <FileViewer
            projectId={projectId ?? ""}
            filePath={filePath}
            lineEnd={lineEnd}
            lineNumber={lineNumber}
            source={source}
            standalone
            viewMode={viewMode}
          />
        ) : (
          <div className="file-viewer">
            <div className="file-viewer-error">
              {relayConfig.error || t("fileInvalidUrl" as never)}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return publicShareContext ? (
    <PublicShareProvider value={publicShareContext}>
      {content}
    </PublicShareProvider>
  ) : (
    content
  );
}
